import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Building2, ArrowRight } from "lucide-react";
import showcaseApi from "../../lib/showcaseApi";

export default function ShowcaseLoginPage() {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // If already signed in, jump straight to dashboard
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await showcaseApi.whoami();
        if (!cancelled && r?.authenticated) {
          navigate("/showcase", { replace: true });
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await showcaseApi.login(password);
      navigate("/showcase", { replace: true });
    } catch (err) {
      setError(err.message || "That password didn't match. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full"
      style={{ background: "hsl(var(--background))" }}
      data-testid="showcase-login-page"
    >
      <div className="showcase-header-band">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
          <div className="flex items-center gap-2">
            <Building2 size={20} style={{ color: "#3F4F66" }} aria-hidden />
            <p className="eyebrow" style={{ color: "#3F4F66" }}>
              LiftTech Portfolio
            </p>
          </div>
          <h1
            className="font-heading text-3xl sm:text-4xl mt-2"
            style={{ color: "#1F1F1F", fontWeight: 600 }}
          >
            Welcome
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2 max-w-prose">
            A calm, plain-English view of what our team is building across the LiftTech ecosystem.
          </p>
        </div>
        <div className="showcase-section-rule" />
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <Card className="showcase-card">
              <CardContent className="p-6 sm:p-8">
                <h2
                  className="font-heading text-2xl"
                  style={{ color: "#1F1F1F", fontWeight: 600 }}
                >
                  Sign in
                </h2>
                <p className="text-sm text-muted-foreground mt-1 mb-6">
                  Please enter the password shared with you.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="showcase-password">Password</Label>
                    <Input
                      id="showcase-password"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder=""
                      data-testid="login-password-input"
                      className="h-11"
                    />
                  </div>
                  {error && (
                    <Alert variant="destructive" data-testid="login-error-alert">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <Button
                    type="submit"
                    disabled={submitting || !password}
                    className="w-full h-11 gap-2"
                    data-testid="login-submit-button"
                    style={{
                      background: "#3F4F66",
                      color: "#F8F8F8",
                    }}
                  >
                    {submitting ? "Signing in…" : "Continue"}
                    {!submitting && <ArrowRight size={16} strokeWidth={2.25} />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-7">
            <div className="space-y-4">
              <h3
                className="font-heading text-xl sm:text-2xl"
                style={{ color: "#1F1F1F", fontWeight: 600 }}
              >
                What you’ll see inside
              </h3>
              <ul className="space-y-3 text-sm sm:text-base leading-relaxed text-foreground max-w-prose">
                <li>
                  <span className="font-semibold">What needs your attention.</span>{" "}
                  The handful of things only the owner can move forward.
                </li>
                <li>
                  <span className="font-semibold">Each product at a glance.</span>{" "}
                  One card per product with a clear “on track / waiting / blocked” status.
                </li>
                <li>
                  <span className="font-semibold">What is blocking us.</span>{" "}
                  A ranked list of the things holding us up, with a plain-English reason.
                </li>
                <li>
                  <span className="font-semibold">Recent wins.</span>{" "}
                  A short timeline of decisions we’ve locked in over the past few days.
                </li>
              </ul>
              <p className="text-xs text-muted-foreground max-w-prose">
                This page pulls the latest from our team’s shared notebook every time you
                open it, then rewrites the technical jargon into plain English before you
                see it. No credentials or account details are ever shown.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
